import { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import ReportFilterBar from '../components/ReportFilterBar';
import { getBookingsReport, getOccupancyReport, listAllGolfCourses } from '../api';
import styles from './BookingsChartPage.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysISO(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().split('T')[0];
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDayTick(iso) {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTH_ABBR[m - 1]} ${d}`;
}

function fmtMonthTick(iso) {
  const [y, m] = iso.split('-').map(Number);
  return `${MONTH_ABBR[m - 1]} '${String(y).slice(2)}`;
}

function sundayOf(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dow = date.getUTCDay();
  date.setUTCDate(d - dow);
  return date.toISOString().split('T')[0];
}

function monthStartOf(iso) {
  return iso.slice(0, 8) + '01';
}

function weekLabel(bucketISO) {
  const [y, m, d] = bucketISO.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, d));
  const end = new Date(Date.UTC(y, m - 1, d + 6));
  const sm = MONTH_ABBR[start.getUTCMonth()];
  const sd = start.getUTCDate();
  const em = MONTH_ABBR[end.getUTCMonth()];
  const ed = end.getUTCDate();
  return `${sm} ${sd} – ${em} ${ed}`;
}

const COURSE_COLORS = [
  '#3b9fd1', '#f59e0b', '#10b981', '#8b5cf6',
  '#ef4444', '#f97316', '#06b6d4', '#ec4899',
];

// Each course gets one hue family. Members = dark shade, Guests = light shade.
const COURSE_HUE_FAMILIES = [
  { member: '#1565C0', guest: '#42A5F5' },  // Blue
  { member: '#E65100', guest: '#FF8A65' },  // Orange
  { member: '#4527A0', guest: '#CE93D8' },  // Purple
  { member: '#00695C', guest: '#80CBC4' },  // Teal
  { member: '#AD1457', guest: '#F48FB1' },  // Rose
  { member: '#4E342E', guest: '#BCAAA4' },  // Brown
];

function aggregateData(teeTimes, granularity) {
  const buckets = new Map();

  for (const tt of teeTimes) {
    let key;
    if (granularity === 'day') {
      key = tt.date;
    } else if (granularity === 'week') {
      key = sundayOf(tt.date);
    } else {
      key = monthStartOf(tt.date);
    }

    const existing = buckets.get(key) || { key, bookings: 0, teeTimes: 0 };
    existing.bookings += (tt.bookings || []).length;
    existing.teeTimes += 1;
    existing.players = (existing.players || 0) + (tt.total_players || 0);
    existing.carts   = (existing.carts   || 0) + (tt.total_cart_count || 0);
    existing.members = (existing.members || 0) + (tt.bookings || []).reduce((s, b) => s + (b.member_count || 0), 0);
    existing.guests  = (existing.guests  || 0) + (tt.bookings || []).reduce((s, b) => s + (b.guest_count  || 0), 0);
    existing.paid    = (existing.paid    || 0) + (tt.bookings || []).reduce((s, b) => s + (b.paid_count   || 0), 0);
    existing.booked  = (existing.booked  || 0) + (tt.bookings || []).reduce((s, b) => s + (b.booked_count || 0), 0);
    existing.noShow  = (existing.noShow  || 0) + (tt.bookings || []).reduce((s, b) => s + (b.no_show_count|| 0), 0);
    buckets.set(key, existing);
  }

  return Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
}

function aggregateDataByCourse(teeTimes, granularity, metric) {
  const buckets = new Map();
  const courseSet = new Set();

  for (const tt of teeTimes) {
    const courseName = tt.golf_course || 'Unknown';
    courseSet.add(courseName);

    let key;
    if (granularity === 'day') {
      key = tt.date;
    } else if (granularity === 'week') {
      key = sundayOf(tt.date);
    } else {
      key = monthStartOf(tt.date);
    }

    const existing = buckets.get(key) || { key };
    const val = metric === 'players'   ? (tt.total_players || 0)
               : metric === 'carts'    ? (tt.total_cart_count || 0)
               : metric === 'bookings' ? (tt.bookings || []).length
               : 1; // teeTimes
    existing[courseName] = (existing[courseName] || 0) + val;
    buckets.set(key, existing);
  }

  const courseNames = Array.from(courseSet).sort();
  const data = Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
  return { data, courseNames };
}

function aggregateMembersGuestsByCourse(teeTimes, granularity) {
  const buckets = new Map();
  const courseSet = new Set();

  for (const tt of teeTimes) {
    const courseName = tt.golf_course || 'Unknown';
    courseSet.add(courseName);

    let key;
    if (granularity === 'day') key = tt.date;
    else if (granularity === 'week') key = sundayOf(tt.date);
    else key = monthStartOf(tt.date);

    const existing = buckets.get(key) || { key };
    const members = (tt.bookings || []).reduce((s, b) => s + (b.member_count || 0), 0);
    const guests  = (tt.bookings || []).reduce((s, b) => s + (b.guest_count  || 0), 0);
    existing[`${courseName}__members`] = (existing[`${courseName}__members`] || 0) + members;
    existing[`${courseName}__guests`]  = (existing[`${courseName}__guests`]  || 0) + guests;
    buckets.set(key, existing);
  }

  const courseNames = Array.from(courseSet).sort();
  const data = Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
  return { data, courseNames };
}

function aggregateOccupancy(occBreakdown, granularity) {
  const buckets = new Map();
  for (const row of (occBreakdown || [])) {
    let key;
    if (granularity === 'day') key = row.date;
    else if (granularity === 'week') key = sundayOf(row.date);
    else key = monthStartOf(row.date);

    const existing = buckets.get(key) || { key, ttAvail: 0, ttBooked: 0, players: 0, maxPlayers: 0 };
    existing.ttAvail    += (row.tee_times_available || 0);
    existing.ttBooked   += (row.tee_times_booked    || 0);
    existing.players    += (row.players             || 0);
    existing.maxPlayers += (row.max_players         || 0);
    buckets.set(key, existing);
  }
  return Array.from(buckets.values())
    .sort((a, b) => (a.key < b.key ? -1 : 1))
    .map(pt => ({
      ...pt,
      occupancy:         pt.ttAvail    > 0 ? Math.round((pt.ttBooked   / pt.ttAvail)    * 100) : 0,
      playerUtilization: pt.maxPlayers > 0 ? Math.round((pt.players    / pt.maxPlayers) * 100) : 0,
    }));
}

function aggregateOccupancyByCourse(occBreakdown, granularity, metric) {
  const buckets = new Map();
  const courseSet = new Set();

  for (const row of (occBreakdown || [])) {
    if (!row.by_course || !row.by_course.length) continue;

    let key;
    if (granularity === 'day') key = row.date;
    else if (granularity === 'week') key = sundayOf(row.date);
    else key = monthStartOf(row.date);

    const existing = buckets.get(key) || { key };
    for (const course of row.by_course) {
      const name = course.golf_course_name;
      courseSet.add(name);
      existing[`${name}__avail`]      = (existing[`${name}__avail`]      || 0) + (course.tee_times_available || 0);
      existing[`${name}__booked`]     = (existing[`${name}__booked`]     || 0) + (course.tee_times_booked    || 0);
      existing[`${name}__players`]    = (existing[`${name}__players`]    || 0) + (course.players             || 0);
      existing[`${name}__maxPlayers`] = (existing[`${name}__maxPlayers`] || 0) + (course.max_players         || 0);
    }
    buckets.set(key, existing);
  }

  const courseNames = Array.from(courseSet).sort();
  const data = Array.from(buckets.values())
    .sort((a, b) => (a.key < b.key ? -1 : 1))
    .map(pt => {
      const result = { key: pt.key };
      for (const name of courseNames) {
        if (metric === 'occupancy') {
          const avail  = pt[`${name}__avail`]  || 0;
          const booked = pt[`${name}__booked`] || 0;
          result[name] = avail > 0 ? Math.round((booked / avail) * 100) : 0;
        } else {
          const players    = pt[`${name}__players`]    || 0;
          const maxPlayers = pt[`${name}__maxPlayers`] || 0;
          result[name] = maxPlayers > 0 ? Math.round((players / maxPlayers) * 100) : 0;
        }
      }
      return result;
    });

  return { data, courseNames };
}

function tickLabel(iso, granularity) {
  if (granularity === 'month') return fmtMonthTick(iso);
  return fmtDayTick(iso);
}

function fmtDateRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return '—';
  return `${fmtDayTick(dateFrom)} – ${fmtDayTick(dateTo)}`;
}

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------

const METRIC_LABELS = {
  bookings:          'Bookings',
  teeTimes:          'Tee times',
  players:           'Players',
  carts:             'Carts needed',
  membersVsGuests:   'Members vs guests',
  statusBreakdown:   'Player status',
  occupancy:         'Occupancy rate',
  playerUtilization: 'Player utilization',
};

const MULTI_LINE_SERIES = {
  membersVsGuests: [
    { key: 'members', label: 'Members', color: '#3b9fd1' },
    { key: 'guests',  label: 'Guests',  color: '#f59e0b' },
  ],
  statusBreakdown: [
    { key: 'paid',   label: 'Paid',    color: '#10b981' },
    { key: 'booked', label: 'Booked',  color: '#3b9fd1' },
    { key: 'noShow', label: 'No show', color: '#f59e0b' },
  ],
};

function ChartTooltip({ active, payload, granularity, metric, breakdown, isMultiLine, isPercentage }) {
  if (!active || !payload || !payload.length) return null;
  const iso = payload[0]?.payload?.key;
  if (!iso) return null;

  let label;
  if (granularity === 'day') label = fmtDayTick(iso);
  else if (granularity === 'week') label = weekLabel(iso);
  else label = fmtMonthTick(iso);

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>{label}</div>
      {(breakdown || isMultiLine) ? (
        payload.map(p => (
          <div key={p.dataKey} className={styles.tooltipRow}>
            <span className={styles.tooltipDot} style={{ background: p.color }} />
            <span className={styles.tooltipCourseName}>{p.name}</span>
            <span className={styles.tooltipCourseVal}>{p.value}{isPercentage ? '%' : ''}</span>
          </div>
        ))
      ) : (
        <div className={styles.tooltipValue}>
          {isPercentage
            ? `${payload[0].value}%`
            : `${payload[0].value} ${(METRIC_LABELS[metric] || metric).toLowerCase()}`}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function BookingsChartPage() {
  const today = todayISO();

  const [dateFrom, setDateFrom] = useState(addDaysISO(today, -29));
  const [dateTo, setDateTo]     = useState(today);
  const [courseId, setCourseId] = useState('');
  const [courses, setCourses]   = useState([]);
  const [granularity, setGranularity] = useState('day');
  const [metric, setMetric]           = useState('bookings');
  const [breakdown, setBreakdown]     = useState(false);

  const [rawTeeTimes, setRawTeeTimes]   = useState(null);
  const [rawOccupancy, setRawOccupancy] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]             = useState(null);

  const [appliedFrom, setAppliedFrom] = useState(null);
  const [appliedTo, setAppliedTo]     = useState(null);

  useEffect(() => {
    listAllGolfCourses()
      .then(r => setCourses(r.items || []))
      .catch(() => {});
  }, []);

  const isMultiLine = metric === 'membersVsGuests' || metric === 'statusBreakdown';
  // membersVsGuests is now compatible with By course; statusBreakdown still isn't
  const effectiveBreakdown = breakdown && !courseId && metric !== 'statusBreakdown';
  const isMembersGuestsByCourse = effectiveBreakdown && metric === 'membersVsGuests';
  const isPercentage = metric === 'occupancy' || metric === 'playerUtilization';

  const { chartData, courseNames } = useMemo(() => {
    if (!rawTeeTimes) return { chartData: [], courseNames: [] };
    if (isPercentage && !effectiveBreakdown) {
      return { chartData: aggregateOccupancy(rawOccupancy || [], granularity), courseNames: [] };
    }
    if (isPercentage && effectiveBreakdown) {
      const result = aggregateOccupancyByCourse(rawOccupancy || [], granularity, metric);
      return { chartData: result.data, courseNames: result.courseNames };
    }
    if (isMembersGuestsByCourse) {
      const result = aggregateMembersGuestsByCourse(rawTeeTimes, granularity);
      return { chartData: result.data, courseNames: result.courseNames };
    }
    if (effectiveBreakdown) {
      const result = aggregateDataByCourse(rawTeeTimes, granularity, metric);
      return { chartData: result.data, courseNames: result.courseNames };
    }
    return { chartData: aggregateData(rawTeeTimes, granularity), courseNames: [] };
  }, [rawTeeTimes, rawOccupancy, granularity, metric, effectiveBreakdown, isMembersGuestsByCourse, isPercentage]);

  const totalValue = useMemo(() => {
    if (metric === 'occupancy') {
      const totBooked = (rawOccupancy || []).reduce((s, r) => s + (r.tee_times_booked    || 0), 0);
      const totAvail  = (rawOccupancy || []).reduce((s, r) => s + (r.tee_times_available || 0), 0);
      return totAvail > 0 ? Math.round((totBooked / totAvail) * 100) : 0;
    }
    if (metric === 'playerUtilization') {
      const totPlayers = (rawOccupancy || []).reduce((s, r) => s + (r.players    || 0), 0);
      const totMax     = (rawOccupancy || []).reduce((s, r) => s + (r.max_players || 0), 0);
      return totMax > 0 ? Math.round((totPlayers / totMax) * 100) : 0;
    }
    if (isMembersGuestsByCourse) {
      return chartData.reduce((sum, pt) =>
        sum + courseNames.reduce((s, name) =>
          s + (pt[`${name}__members`] || 0) + (pt[`${name}__guests`] || 0), 0), 0);
    }
    if (effectiveBreakdown) {
      return chartData.reduce((sum, pt) =>
        sum + courseNames.reduce((s, c) => s + (pt[c] || 0), 0), 0);
    }
    if (metric === 'membersVsGuests') {
      return chartData.reduce((s, pt) => s + (pt.members || 0) + (pt.guests || 0), 0);
    }
    if (metric === 'statusBreakdown') {
      return chartData.reduce((s, pt) => s + (pt.paid || 0) + (pt.booked || 0) + (pt.noShow || 0), 0);
    }
    return chartData.reduce((s, pt) => s + (pt[metric] || 0), 0);
  }, [chartData, metric, effectiveBreakdown, isMembersGuestsByCourse, courseNames, isPercentage, rawOccupancy]);

  const handleApply = () => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setRawTeeTimes(null);
    setRawOccupancy(null);
    setLoading(true);
    setError(null);
    Promise.all([
      getBookingsReport({ dateFrom, dateTo, courseId: courseId || undefined }),
      getOccupancyReport({ dateFrom, dateTo, courseId: courseId || undefined }),
    ])
      .then(([bookingsData, occData]) => {
        setRawTeeTimes(bookingsData.tee_times || []);
        setRawOccupancy(occData.breakdown || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Request failed');
        setLoading(false);
      });
  };

  const showDots = chartData.length <= 60;

  return (
    <div className={styles.page}>
      <div className={styles.toolbarWrap}>
        <ReportFilterBar
          title="Bookings chart"
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          courseId={courseId}
          courses={courses}
          onCourseChange={setCourseId}
          onApply={handleApply}
          extraFilters={
            <>
              <label className={styles.label}>Metric:</label>
              <select
                className={styles.select}
                value={metric}
                onChange={e => setMetric(e.target.value)}
              >
                <option value="bookings">Bookings</option>
                <option value="teeTimes">Tee times</option>
                <option value="players"># of players</option>
                <option value="membersVsGuests">Members vs guests</option>
                <option value="statusBreakdown" disabled={breakdown && !courseId}>Player status breakdown</option>
                <option value="carts"># of carts needed</option>
                <option value="occupancy">Occupancy rate</option>
                <option value="playerUtilization">Player utilization</option>
              </select>
              <label className={styles.label}>Granularity:</label>
              <select
                className={styles.select}
                value={granularity}
                onChange={e => setGranularity(e.target.value)}
              >
                <option value="day">Per day</option>
                <option value="week">Per week</option>
                <option value="month">Per month</option>
              </select>
              <label className={`${styles.toggleLabel}${courseId ? ` ${styles.toggleLabelDisabled}` : ''}`}>
                <input
                  type="checkbox"
                  className={styles.toggleCheckbox}
                  checked={breakdown && !courseId}
                  disabled={!!courseId || metric === 'statusBreakdown'}
                  onChange={e => setBreakdown(e.target.checked)}
                />
                By course
              </label>
            </>
          }
        />
      </div>

      <div className={styles.body}>
        {rawTeeTimes === null && !loading && !error && (
          <div className={styles.emptyState}>
            Set a date range and click Apply
          </div>
        )}

        {loading && (
          <div className={styles.emptyState}>Loading…</div>
        )}

        {error && !loading && (
          <div className={styles.errorState}>{error}</div>
        )}

        {!loading && !error && rawTeeTimes !== null && chartData.length === 0 && (
          <div className={styles.emptyState}>
            No bookings found for this date range.
          </div>
        )}

        {!loading && !error && rawTeeTimes !== null && chartData.length > 0 && (
          <>
            <div className={styles.statsBar}>
              <div className={styles.statPill}>
                <span className={styles.statLabel}>
                  {isPercentage
                    ? METRIC_LABELS[metric]
                    : isMultiLine
                      ? 'Total players'
                      : `Total ${METRIC_LABELS[metric].toLowerCase()}`}
                </span>
                <span className={styles.statValue}>{totalValue}{isPercentage ? '%' : ''}</span>
              </div>
              <div className={styles.statPill}>
                <span className={styles.statLabel}>Date range</span>
                <span className={styles.statValue}>{fmtDateRange(appliedFrom, appliedTo)}</span>
              </div>
              <div className={styles.statPill}>
                <span className={styles.statLabel}>Data points</span>
                <span className={styles.statValue}>{chartData.length}</span>
              </div>
            </div>

            <div className={styles.chartCard}>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#f0f1f3" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="key"
                    tickFormatter={iso => tickLabel(iso, granularity)}
                    interval="preserveStartEnd"
                    tick={{ fontSize: 11, fill: 'var(--cp-gray-500)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    domain={isPercentage ? [0, 100] : [0, 'auto']}
                    tickFormatter={isPercentage ? v => `${v}%` : undefined}
                    width={isPercentage ? 44 : 36}
                    tick={{ fontSize: 11, fill: 'var(--cp-gray-500)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<ChartTooltip granularity={granularity} metric={metric} breakdown={effectiveBreakdown} isMultiLine={isMultiLine} isPercentage={isPercentage} />}
                    cursor={{ stroke: 'var(--cp-gray-200)', strokeWidth: 1 }}
                  />
                  {(effectiveBreakdown || isMultiLine) && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />}
                  {isMembersGuestsByCourse ? (
                    courseNames.flatMap((name, i) => {
                      const family = COURSE_HUE_FAMILIES[i % COURSE_HUE_FAMILIES.length];
                      return [
                        <Line
                          key={`${name}__members`}
                          type="linear"
                          dataKey={`${name}__members`}
                          name={`${name} — Members`}
                          stroke={family.member}
                          strokeWidth={2}
                          dot={showDots ? { r: 3, fill: family.member, strokeWidth: 0 } : false}
                          activeDot={{ r: 5, strokeWidth: 0 }}
                        />,
                        <Line
                          key={`${name}__guests`}
                          type="linear"
                          dataKey={`${name}__guests`}
                          name={`${name} — Guests`}
                          stroke={family.guest}
                          strokeWidth={2}
                          dot={showDots ? { r: 3, fill: family.guest, strokeWidth: 0 } : false}
                          activeDot={{ r: 5, strokeWidth: 0 }}
                        />,
                      ];
                    })
                  ) : effectiveBreakdown ? (
                    courseNames.map((name, i) => (
                      <Line
                        key={name}
                        type="linear"
                        dataKey={name}
                        name={name}
                        stroke={COURSE_COLORS[i % COURSE_COLORS.length]}
                        strokeWidth={2}
                        dot={showDots ? { r: 3, fill: COURSE_COLORS[i % COURSE_COLORS.length], strokeWidth: 0 } : false}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                      />
                    ))
                  ) : isMultiLine ? (
                    MULTI_LINE_SERIES[metric].map(series => (
                      <Line
                        key={series.key}
                        type="linear"
                        dataKey={series.key}
                        name={series.label}
                        stroke={series.color}
                        strokeWidth={2}
                        dot={showDots ? { r: 3, fill: series.color, strokeWidth: 0 } : false}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                      />
                    ))
                  ) : (
                    <Line
                      type="linear"
                      dataKey={metric}
                      stroke="#3b9fd1"
                      strokeWidth={2}
                      dot={showDots ? { r: 3, fill: '#3b9fd1', strokeWidth: 0 } : false}
                      activeDot={{ r: 5, fill: '#3b9fd1', strokeWidth: 0 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
